import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  readableInk,
  todayIn,
  UPLOAD_CONTENT_TYPES,
  type Attachment,
  type ListItem,
  type ListItemMark,
  type ListItemMarkCreate,
  type ListItemPatch,
} from '@jobshop/shared';
import { downloadFile, fetchBlob, openInNewTab } from '../../api/client';
import {
  useAddAttachment,
  useAddJobAttachment,
  useAddListItem,
  useAddListItemMark,
  useDeleteAttachment,
  useDeleteJobAttachment,
  useDeleteListItem,
  useDeleteListItemMark,
  useJob,
  useJobListItems,
  usePatchListItem,
  useShopTimeZone,
} from '../../api/hooks';
import { useSession } from '../../auth/useUser';
import { JobNav } from './JobNav';
import {
  Card,
  CardHeader,
  Empty,
  ErrorNote,
  Field,
  Modal,
  Spinner,
} from '../../components/ui';

/**
 * The page a customer opens far more often than the plan: a todo list and running
 * notes for the job, mixed together. A living spreadsheet, not a form — every cell is
 * edited in place, and "Add item" appends a blank row rather than opening a dialog.
 */
export function JobListPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const job = useJob(jobId);
  const items = useJobListItems(jobId);
  const session = useSession();
  const canEdit = session.can('planner');
  const today = todayIn(useShopTimeZone());
  const patchItem = usePatchListItem(jobId ?? '');
  const deleteItem = useDeleteListItem(jobId ?? '');
  const addItem = useAddListItem(jobId ?? '');
  const addAttachment = useAddAttachment(jobId ?? '');
  const deleteAttachment = useDeleteAttachment(jobId ?? '');
  const addMark = useAddListItemMark(jobId ?? '');
  const deleteMark = useDeleteListItemMark(jobId ?? '');
  const [drafts, setDrafts] = useState<DraftItem[]>([]);

  if (job.isLoading) return <Spinner />;
  if (job.error) return <ErrorNote error={job.error} />;
  if (!job.data) return null;
  const jobData = job.data;

  // Open items first, soonest due first, undated open items last; done items trail.
  const rows = [...(items.data ?? [])].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    const an = a.neededBy ?? '9999-99-99';
    const bn = b.neededBy ?? '9999-99-99';
    if (an !== bn) return an.localeCompare(bn);
    return String(a.createdAt).localeCompare(String(b.createdAt));
  });

  function updateDraft(key: string, patch: Partial<DraftItem>) {
    setDrafts((d) => d.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function discardDraft(key: string) {
    setDrafts((d) => d.filter((r) => r.key !== key));
  }

  /** Fires when focus leaves the row. A blank "Item" just leaves the row sitting there
   * unsaved — nothing is lost, there is simply nothing to send yet. */
  function commitDraft(key: string) {
    const row = drafts.find((r) => r.key === key);
    if (!row || row.saving || row.work.trim() === '') return;
    updateDraft(key, { saving: true });
    addItem.mutate(
      {
        work: row.work.trim(),
        ...(row.description.trim() ? { description: row.description.trim() } : {}),
        ...(row.assignedTo.trim() ? { assignedTo: row.assignedTo.trim() } : {}),
        ...(row.neededBy ? { neededBy: row.neededBy } : {}),
      },
      {
        onSuccess: () => discardDraft(key),
        onError: () => updateDraft(key, { saving: false }),
      },
    );
  }

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Link to="/jobs" className="text-ink-400 hover:underline">
                Jobs
              </Link>
              <span className="text-ink-300">/</span>
              <span
                className="rounded px-1.5 py-0.5"
                style={{ backgroundColor: jobData.color, color: readableInk(jobData.color) }}
              >
                {jobData.name}
              </span>
              <JobNav
                jobId={jobData._id}
                current="list"
                partsBatchId={jobData.batches.length === 1 ? jobData.batches[0]!._id : undefined}
              />
            </span>
          }
          actions={
            canEdit ? (
              <button className="btn-primary" onClick={() => setDrafts((d) => [...d, blankDraft()])}>
                Add item
              </button>
            ) : null
          }
        />

        {items.isLoading ? <Spinner /> : null}
        {items.error ? (
          <div className="p-4">
            <ErrorNote error={items.error} />
          </div>
        ) : null}
        {rows.length === 0 && drafts.length === 0 ? (
          <Empty>Nothing on the list yet.</Empty>
        ) : null}

        {rows.length > 0 || drafts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] table-fixed text-sm">
              <thead className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="w-36 px-1 py-2 font-medium">Item</th>
                  <th className="px-1 py-2 font-medium">Description</th>
                  <th className="w-[108px] px-1 py-2 font-medium">Assigned to</th>
                  <th className="w-[163px] px-1 py-2 font-medium">Needed by</th>
                  <th className="w-44 px-1 py-2 font-medium">Attachments</th>
                  {canEdit ? <th className="w-20 px-2 py-2 font-medium" /> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <SavedRow
                    key={item._id}
                    item={item}
                    today={today}
                    canEdit={canEdit}
                    onPatch={(input) => patchItem.mutate({ itemId: item._id, input })}
                    onDelete={() => {
                      if (confirm(`Delete "${item.work}"?`)) deleteItem.mutate(item._id);
                    }}
                    onUpload={(file) => addAttachment.mutate({ itemId: item._id, file })}
                    onAddMark={(input) => addMark.mutate({ itemId: item._id, input })}
                    onDeleteMark={(markId) => deleteMark.mutate({ itemId: item._id, markId })}
                    onRemoveAttachment={(attachmentId) =>
                      deleteAttachment.mutate({ itemId: item._id, attachmentId })
                    }
                    uploading={
                      addAttachment.isPending && addAttachment.variables?.itemId === item._id
                    }
                  />
                ))}
                {canEdit
                  ? drafts.map((row) => (
                      <DraftRow
                        key={row.key}
                        row={row}
                        onChange={(patch) => updateDraft(row.key, patch)}
                        onCommit={() => commitDraft(row.key)}
                        onDiscard={() => discardDraft(row.key)}
                      />
                    ))
                  : null}
              </tbody>
            </table>
          </div>
        ) : null}

        <div className="px-4 py-2">
          <ErrorNote
            error={
              patchItem.error ??
              deleteItem.error ??
              addItem.error ??
              addAttachment.error ??
              deleteAttachment.error ??
              addMark.error ??
              deleteMark.error
            }
          />
        </div>
      </Card>

      <ReferenceAttachments jobId={jobId ?? ''} attachments={jobData.attachments} canEdit={canEdit} />
    </div>
  );
}

/** Grows a textarea to fit its content, so the full description reads at a glance
 * instead of behind a scrollbar or a manual drag-resize. */
function autoResizeTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

function SavedRow({
  item,
  today,
  canEdit,
  onPatch,
  onDelete,
  onUpload,
  onRemoveAttachment,
  onAddMark,
  onDeleteMark,
  uploading,
}: {
  item: ListItem;
  today: string;
  canEdit: boolean;
  onPatch: (input: ListItemPatch) => void;
  onDelete: () => void;
  onUpload: (file: File) => void;
  onRemoveAttachment: (attachmentId: string) => void;
  onAddMark: (input: ListItemMarkCreate) => void;
  onDeleteMark: (markId: string) => void;
  uploading: boolean;
}) {
  const late = !!item.neededBy && !item.done && item.neededBy < today;
  const commitText = (
    field: 'work' | 'description' | 'assignedTo',
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const v = e.target.value.trim();
    const current = item[field] ?? '';
    if (field === 'work' && v === '') {
      e.target.value = item.work; // "Work" cannot be blanked out
      return;
    }
    if (v === current) return;
    onPatch({ [field]: v === '' ? null : v } as ListItemPatch);
  };
  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  return (
    <tr className={clsx('border-b border-ink-100 last:border-0', item.done && 'bg-green-50')}>
      <td className="w-36 px-1 py-1">
        <input
          key={item.work}
          className={clsx('cell-input font-medium', item.done && 'text-ink-400 line-through')}
          defaultValue={item.work}
          disabled={!canEdit}
          onKeyDown={blurOnEnter}
          onBlur={(e) => commitText('work', e)}
        />
      </td>
      <td className="px-1 py-1">
        {item.marks.length > 0 || canEdit ? (
          <div className="mb-1 flex flex-wrap items-center gap-1">
            {item.marks.map((mark) => (
              <MarkChip
                key={mark._id}
                mark={mark}
                canEdit={canEdit}
                onDelete={() => onDeleteMark(mark._id)}
              />
            ))}
            {canEdit ? <AddMarkButton today={today} onAdd={onAddMark} /> : null}
          </div>
        ) : null}
        <textarea
          key={item.description ?? ''}
          ref={autoResizeTextarea}
          className={clsx('cell-input resize-none overflow-hidden', item.done && 'text-ink-400')}
          defaultValue={item.description ?? ''}
          disabled={!canEdit}
          rows={1}
          onInput={(e) => autoResizeTextarea(e.currentTarget)}
          onBlur={(e) => commitText('description', e)}
        />
      </td>
      <td className="w-[108px] px-1 py-1">
        <input
          key={item.assignedTo ?? ''}
          className={clsx('cell-input', item.done && 'text-ink-400')}
          defaultValue={item.assignedTo ?? ''}
          disabled={!canEdit}
          onKeyDown={blurOnEnter}
          onBlur={(e) => commitText('assignedTo', e)}
        />
      </td>
      <td className="w-[163px] px-1 py-1">
        <input
          key={item.neededBy ?? ''}
          type="date"
          className={clsx(
            'cell-input',
            late ? 'font-medium text-red-700' : item.done && 'text-ink-400',
          )}
          defaultValue={item.neededBy ?? ''}
          disabled={!canEdit}
          onBlur={(e) => {
            const v = e.target.value;
            if (v === (item.neededBy ?? '')) return;
            onPatch({ neededBy: v === '' ? null : v });
          }}
        />
        <button
          type="button"
          className="mt-1 flex items-center gap-1 disabled:cursor-default"
          disabled={!canEdit}
          onClick={() => onPatch({ done: !item.done })}
          aria-label={item.done ? 'Mark not done' : 'Mark done'}
        >
          <span
            className={clsx(
              'flex h-4 w-4 items-center justify-center rounded border text-[10px] leading-none',
              item.done ? 'border-green-600 bg-green-500 text-white' : 'border-ink-300 text-transparent',
            )}
          >
            ✓
          </span>
          <span className={clsx('text-xs', item.done ? 'text-green-700' : 'text-ink-400')}>done</span>
        </button>
      </td>
      <td className="w-44 px-1 py-1">
        <AttachmentsCell
          jobId={item.jobId}
          itemId={item._id}
          attachments={item.attachments}
          canEdit={canEdit}
          uploading={uploading}
          onUpload={onUpload}
          onRemove={onRemoveAttachment}
        />
      </td>
      {canEdit ? (
        <td className="w-20 px-2 py-1 text-right">
          <button
            type="button"
            className="btn-ghost px-1.5 py-0.5 text-xs text-red-700"
            onClick={onDelete}
          >
            Delete
          </button>
        </td>
      ) : null}
    </tr>
  );
}

/**
 * The "special items" a row can carry above its description. One kind so far; the
 * record is what a second one would be added to, and what the ⊕ menu is built from.
 */
const MARK_LABELS: Record<ListItemMark['kind'], string> = {
  dateRequested: 'Date of request',
};

/** A recorded detail, read-only by design — a wrong one is deleted and added again. */
function MarkChip({
  mark,
  canEdit,
  onDelete,
}: {
  mark: ListItemMark;
  canEdit: boolean;
  onDelete: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-600">
      <span>
        {MARK_LABELS[mark.kind]}: {mark.date}
        {mark.by ? ` by ${mark.by}` : ''}
      </span>
      {canEdit ? (
        <button
          type="button"
          className="text-ink-400 hover:text-red-700"
          aria-label={`Remove ${MARK_LABELS[mark.kind]}`}
          onClick={onDelete}
        >
          ×
        </button>
      ) : null}
    </span>
  );
}

/** Roughly how tall the popover gets — enough to decide whether it opens downwards. */
const MARK_POPOVER_HEIGHT = 230;

/** The ⊕ beside the chips: a menu of kinds, then a small form for the one chosen.
 * A popover rather than a modal, to keep the page's edit-in-place feel. It is
 * positioned `fixed` against the button's own rect on purpose — the table scrolls
 * sideways in an `overflow-x-auto` box, which would clip an absolutely positioned
 * one out of sight. */
function AddMarkButton({ today, onAdd }: { today: string; onAdd: (input: ListItemMarkCreate) => void }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<ListItemMark['kind'] | null>(null);
  const [date, setDate] = useState(today);
  const [by, setBy] = useState('');
  const [at, setAt] = useState({ top: 0, left: 0 });
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  function openAt() {
    const rect = button.current?.getBoundingClientRect();
    if (rect) {
      const below = window.innerHeight - rect.bottom > MARK_POPOVER_HEIGHT;
      setAt({
        top: below ? rect.bottom + 4 : Math.max(4, rect.top - MARK_POPOVER_HEIGHT),
        left: Math.min(rect.left, window.innerWidth - 272),
      });
    }
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setKind(null);
    setDate(today);
    setBy('');
  }

  // Clicking anywhere else, or Escape, dismisses it — nothing here is worth a confirm.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, today]);

  return (
    <span ref={root} className="inline-flex">
      <button
        ref={button}
        type="button"
        className="flex h-4 w-4 items-center justify-center rounded-full bg-ink-200 text-[11px] leading-none text-ink-600 hover:bg-ink-300"
        title="Add a detail to this description"
        aria-label="Add a detail to this description"
        aria-expanded={open}
        onClick={() => (open ? close() : openAt())}
      >
        +
      </button>

      {open ? (
        <div
          className="fixed z-30 w-64 rounded-md border border-ink-200 bg-white p-2 shadow-lg"
          style={{ top: at.top, left: at.left }}
        >
          {kind === null ? (
            <ul>
              {(Object.keys(MARK_LABELS) as ListItemMark['kind'][]).map((k) => (
                <li key={k}>
                  <button
                    type="button"
                    className="w-full rounded px-2 py-1 text-left text-sm hover:bg-ink-100"
                    onClick={() => setKind(k)}
                  >
                    {MARK_LABELS[k]}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <form
              className="space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!date) return;
                onAdd({ kind, date, ...(by.trim() ? { by: by.trim() } : {}) });
                close();
              }}
            >
              <Field label={MARK_LABELS[kind]}>
                <input
                  type="date"
                  className="field"
                  value={date}
                  autoFocus
                  aria-label={MARK_LABELS[kind]}
                  onChange={(e) => setDate(e.target.value)}
                />
              </Field>
              <Field label="Requested by" hint="Optional.">
                <input
                  className="field"
                  value={by}
                  placeholder="who asked"
                  onChange={(e) => setBy(e.target.value)}
                />
              </Field>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn-ghost text-xs" onClick={close}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary text-xs" disabled={!date}>
                  Add
                </button>
              </div>
            </form>
          )}
        </div>
      ) : null}
    </span>
  );
}

/** Fetches one rendition of an attachment (`/thumb` or `/page`) into an object URL —
 * the route needs a bearer token, so a plain `<img src>` can't do it. */
function useAttachmentImage(path: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    void fetchBlob(path).then((blob) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  return url;
}

/** An image attachment: its one page (`pages/1/thumb`) as a small thumbnail; clicking
 * it opens the larger `pages/1/full` enlarged, rather than downloading — that stays
 * the job of "Save Original" and the plain filename link used for anything that isn't
 * renderable. */
function ImageAttachmentThumb({ path, filename }: { path: string; filename: string }) {
  const [open, setOpen] = useState(false);
  const thumbUrl = useAttachmentImage(`${path}/pages/1/thumb`);
  const pageUrl = useAttachmentImage(open ? `${path}/pages/1/full` : null);

  if (!thumbUrl) return <span className="block h-14 w-14 animate-pulse rounded bg-ink-200" />;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}>
        <img src={thumbUrl} alt="" className="h-14 w-14 rounded object-cover" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title={filename}>
        <div className="space-y-3">
          {pageUrl ? (
            <img src={pageUrl} alt={filename} className="mx-auto max-h-[75vh] max-w-full object-contain" />
          ) : (
            <Spinner />
          )}
          <div className="flex justify-end">
            <button type="button" className="btn-ghost" onClick={() => downloadFile(path, filename)}>
              Save Original
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/** A PDF attachment: page 1's thumbnail (when rendering succeeded) plus the filename
 * in bold blue, styled and behaving like a link. Either one opens the original PDF
 * itself in a new tab — the shop wants the browser's own PDF viewer, not our
 * rendered page — so there is no enlarge modal here the way there is for an image. */
function PdfAttachmentItem({ path, filename, hasThumb }: { path: string; filename: string; hasThumb: boolean }) {
  const thumbUrl = useAttachmentImage(hasThumb ? `${path}/pages/1/thumb` : null);

  return (
    <button
      type="button"
      className="flex flex-col items-center gap-0.5"
      title={filename}
      onClick={() => void openInNewTab(path)}
    >
      {hasThumb ? (
        thumbUrl ? (
          <img src={thumbUrl} alt="" className="h-14 w-14 rounded object-cover" />
        ) : (
          <span className="block h-14 w-14 animate-pulse rounded bg-ink-200" />
        )
      ) : null}
      <span className="max-w-[6.5rem] truncate text-xs font-bold text-blue-700 hover:underline">{filename}</span>
    </button>
  );
}

/** One attachment's chip — thumbnail/filename plus an optional remove button. Shared
 * by a list item's own Attachments cell and the job-level reference attachments
 * section at the bottom of the page: both render an attachment the same way, just
 * against a different path and a different owning record. */
function AttachmentChip({
  path,
  attachment,
  canEdit,
  onRemove,
}: {
  path: string;
  attachment: Attachment;
  canEdit: boolean;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded bg-ink-100 px-1.5 py-0.5 text-xs">
      {attachment.contentType === 'application/pdf' ? (
        <PdfAttachmentItem path={path} filename={attachment.filename} hasThumb={attachment.pages > 0} />
      ) : attachment.pages > 0 ? (
        <ImageAttachmentThumb path={path} filename={attachment.filename} />
      ) : (
        <button
          type="button"
          className="max-w-[10rem] truncate hover:underline"
          title={attachment.filename}
          onClick={() => downloadFile(path, attachment.filename)}
        >
          {attachment.filename}
        </button>
      )}
      {canEdit ? (
        <button
          type="button"
          className="text-ink-400 hover:text-red-700"
          aria-label={`Remove ${attachment.filename}`}
          onClick={() => {
            if (confirm(`Remove "${attachment.filename}"?`)) onRemove();
          }}
        >
          ×
        </button>
      ) : null}
    </span>
  );
}

/** A hidden file input behind a "+ Add" button — the same upload-on-select pattern
 * used everywhere in this app that avoids a modal. */
function AddAttachmentButton({
  uploading,
  onUpload,
}: {
  uploading: boolean;
  onUpload: (file: File) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={fileInput}
        type="file"
        // The same list the server enforces — this only spares someone the round trip
        // of picking a file that would come back a 415.
        accept={UPLOAD_CONTENT_TYPES.join(',')}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onUpload(file);
        }}
      />
      <button
        type="button"
        className="btn-ghost px-1.5 py-0.5 text-xs"
        disabled={uploading}
        onClick={() => fileInput.current?.click()}
      >
        {uploading ? 'Uploading…' : '+ Add'}
      </button>
    </>
  );
}

function AttachmentsCell({
  jobId,
  itemId,
  attachments,
  canEdit,
  uploading,
  onUpload,
  onRemove,
}: {
  jobId: string;
  itemId: string;
  attachments: ListItem['attachments'];
  canEdit: boolean;
  uploading: boolean;
  onUpload: (file: File) => void;
  onRemove: (attachmentId: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {attachments.map((a) => (
        <AttachmentChip
          key={a._id}
          path={`/jobs/${jobId}/list-items/${itemId}/attachments/${a._id}`}
          attachment={a}
          canEdit={canEdit}
          onRemove={() => onRemove(a._id)}
        />
      ))}
      {canEdit ? <AddAttachmentButton uploading={uploading} onUpload={onUpload} /> : null}
    </div>
  );
}

/** The bottom-of-page section for material that belongs to the job as a whole rather
 * than to one List row — a drawing, a PO, anything that doesn't fit a row. Same
 * upload/storage/display as a list item's own attachments, no other columns. */
function ReferenceAttachments({
  jobId,
  attachments,
  canEdit,
}: {
  jobId: string;
  attachments: Attachment[];
  canEdit: boolean;
}) {
  const addAttachment = useAddJobAttachment(jobId);
  const deleteAttachment = useDeleteJobAttachment(jobId);

  return (
    <Card>
      <CardHeader
        title="Reference attachments"
        subtitle="Drawings, POs, or anything else that belongs to the job as a whole rather than one item above."
      />
      {attachments.length === 0 && !canEdit ? (
        <Empty>No reference attachments.</Empty>
      ) : (
        <div className="flex flex-wrap items-center gap-1 p-4">
          {attachments.map((a) => (
            <AttachmentChip
              key={a._id}
              path={`/jobs/${jobId}/attachments/${a._id}`}
              attachment={a}
              canEdit={canEdit}
              onRemove={() => deleteAttachment.mutate(a._id)}
            />
          ))}
          {canEdit ? (
            <AddAttachmentButton
              uploading={addAttachment.isPending}
              onUpload={(file) => addAttachment.mutate(file)}
            />
          ) : null}
        </div>
      )}
      <div className="px-4 pb-2">
        <ErrorNote error={addAttachment.error ?? deleteAttachment.error} />
      </div>
    </Card>
  );
}

interface DraftItem {
  key: string;
  work: string;
  description: string;
  assignedTo: string;
  neededBy: string;
  saving: boolean;
}

function blankDraft(): DraftItem {
  return {
    key: crypto.randomUUID(),
    work: '',
    description: '',
    assignedTo: '',
    neededBy: '',
    saving: false,
  };
}

/** An unsaved row — controlled locally until "Work" is filled and focus leaves it,
 * at which point it is created for real (see `commitDraft`) and this disappears in
 * favour of the row the server now returns. */
function DraftRow({
  row,
  onChange,
  onCommit,
  onDiscard,
}: {
  row: DraftItem;
  onChange: (patch: Partial<DraftItem>) => void;
  onCommit: () => void;
  onDiscard: () => void;
}) {
  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  return (
    <tr
      className="border-b border-ink-100 last:border-0"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onCommit();
      }}
    >
      <td className="w-36 px-1 py-1">
        <input
          className="cell-input font-medium"
          placeholder="What needs doing…"
          autoFocus
          value={row.work}
          disabled={row.saving}
          onChange={(e) => onChange({ work: e.target.value })}
          onKeyDown={blurOnEnter}
        />
      </td>
      <td className="px-1 py-1">
        <textarea
          ref={autoResizeTextarea}
          className="cell-input resize-none overflow-hidden"
          rows={1}
          value={row.description}
          disabled={row.saving}
          onChange={(e) => {
            onChange({ description: e.target.value });
            autoResizeTextarea(e.currentTarget);
          }}
        />
      </td>
      <td className="w-[108px] px-1 py-1">
        <input
          className="cell-input"
          value={row.assignedTo}
          disabled={row.saving}
          onChange={(e) => onChange({ assignedTo: e.target.value })}
          onKeyDown={blurOnEnter}
        />
      </td>
      <td className="w-[163px] px-1 py-1">
        <input
          type="date"
          className="cell-input"
          value={row.neededBy}
          disabled={row.saving}
          onChange={(e) => onChange({ neededBy: e.target.value })}
        />
        <div className="mt-1 flex items-center gap-1">
          <span className="flex h-4 w-4 items-center justify-center rounded border border-ink-200 text-[10px] leading-none text-transparent">
            ✓
          </span>
          <span className="text-xs text-ink-300">done</span>
        </div>
      </td>
      <td className="w-44 px-1 py-1 text-xs text-ink-300">Save the row first</td>
      <td className="w-20 px-2 py-1 text-right">
        <button
          type="button"
          className="btn-ghost px-1.5 py-0.5 text-xs text-red-700"
          disabled={row.saving}
          onClick={onDiscard}
        >
          {row.saving ? 'Saving…' : 'Discard'}
        </button>
      </td>
    </tr>
  );
}
